import React, { useEffect, useState } from 'react'
import { clientDatabasesAPI } from '@hpanel/web-common/api/client'

/**
 * "Open phpMyAdmin" button: mints a one-shot SSO URL via
 * `POST /api/client/databases/<id>/pma-link` and opens it in a new
 * tab. The token expires in ~60s; we display a countdown so the user
 * knows roughly how long the URL stays valid.
 *
 * §9 contract:
 *   - 503 → render the friendly "phpMyAdmin not configured" message.
 *     The server's `detail` (when present) takes priority — that
 *     comes from fetchAPI which preserves it verbatim.
 *   - Any other failure → clean status-tied text from fetchAPI.
 *   - Network error → "Unable to reach server" via fetchAPI's
 *     TypeError catch. We never surface the raw browser message.
 */

export interface PhpMyAdminButtonProps {
  dbId: number
  dbUserId: number
  /**
   * Optional label override — defaults to "Open phpMyAdmin". Used by
   * the manage-users modal to render shorter "Open" buttons in the
   * row context.
   */
  label?: string
  /** Visual variant for table rows vs. primary actions. */
  variant?: 'primary' | 'inline'
}

export const PhpMyAdminButton: React.FC<PhpMyAdminButtonProps> = ({
  dbId,
  dbUserId,
  label = 'Open phpMyAdmin',
  variant = 'primary',
}) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [now, setNow] = useState(() => Date.now())

  // Tick a 1Hz timer while a freshly-minted link is still valid so the
  // countdown badge updates. We only run the timer when there's
  // something to count down — keeps the page idle otherwise.
  useEffect(() => {
    if (expiresAt == null) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [expiresAt])

  const remainingSec =
    expiresAt == null ? 0 : Math.max(0, Math.ceil((expiresAt - now) / 1000))

  // Auto-clear the countdown once the token is dead.
  useEffect(() => {
    if (expiresAt != null && remainingSec === 0) {
      setExpiresAt(null)
    }
  }, [expiresAt, remainingSec])

  const onClick = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const link = await clientDatabasesAPI.mintPmaLink(dbId, dbUserId)
      // Open in a new tab; noopener+noreferrer to prevent the popup
      // from controlling the originating window's navigation.
      window.open(link.url, '_blank', 'noopener,noreferrer')
      const exp = Date.parse(link.expires_at)
      if (!Number.isNaN(exp)) setExpiresAt(exp)
    } catch (e) {
      // fetchAPI already gives us a clean message — preserve verbatim,
      // including the server's `detail` (e.g. "phpMyAdmin not
      // configured"). We log the raw error for the dev console.
      // eslint-disable-next-line no-console
      console.error('[PhpMyAdminButton] mintPmaLink failed', e)
      setError(e instanceof Error ? e.message : 'phpMyAdmin not available')
    } finally {
      setBusy(false)
    }
  }

  const baseClass =
    variant === 'primary'
      ? 'btn-secondary text-sm whitespace-nowrap'
      : 'text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50'

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        data-testid={`pma-button-${dbId}-${dbUserId}`}
        className={`${baseClass} disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {busy ? 'Opening…' : label}
      </button>
      {expiresAt != null && remainingSec > 0 && (
        <span
          data-testid={`pma-countdown-${dbId}-${dbUserId}`}
          className="text-xs text-gray-500 dark:text-gray-400"
          title="Time remaining on the SSO link"
        >
          ({remainingSec}s)
        </span>
      )}
      {error && (
        <span
          role="alert"
          data-testid={`pma-error-${dbId}-${dbUserId}`}
          className="text-xs text-red-600 dark:text-red-400"
        >
          {error}
        </span>
      )}
    </span>
  )
}
